import React, { useState, useEffect, useCallback } from 'react';
import {
    FiRefreshCw,
    FiActivity,
    FiTrendingUp,
    FiClock,
    FiAlertTriangle,
    FiCheckCircle,
    FiXCircle,
    FiZap,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import api from '../../libs/axios';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Response shape of GET /admin/sportmonks/usage (payload inside the envelope)
interface HourUsage {
    hour: string; // UTC 'YYYY-MM-DDTHH'
    total: number;
    byEndpoint: Record<string, number>;
}
interface CurrentHour extends HourUsage {
    budget: number;
    remaining: number;
}
interface RateLimit {
    remaining: number | null;
    limit: number | null;
    resetsAt: string | null;
    capturedAt: string | null;
}
// SportMonks' OWN per-entity quota. `used` is derived as limit - remaining, so
// the gauge tracks SportMonks' real window rather than our clock-hour counter.
// windowStartedAt/windowResetsAt come from the backend detecting `remaining`
// jumping back up (SportMonks sends no reset header).
interface EntityRate {
    limit: number | null;
    remaining: number | null;
    updatedAt: string | null;
    windowStartedAt: string | null;
    windowResetsAt: string | null;
}
interface ThrottleStat {
    count: number;
    lastAt: string;
}
interface Throttles {
    hour: string;
    total: number;
    byEntity: Record<string, ThrottleStat>;
}
interface Poller {
    tokenConfigured: boolean;
    isPoller: boolean;
    running: boolean;
    livePollMs: number;
    lastPollAt: string | null;
    liveFixtures: number;
    trackedLive: number;
    lastWindowSyncAt: string | null;
    pacingGapMs: number;
}
interface UsagePayload {
    currentHour: CurrentHour;
    rateLimit: RateLimit;
    entityRates: Record<string, EntityRate>;
    throttles: Throttles;
    poller: Poller;
    history: HourUsage[];
    peakHour: HourUsage | null;
    rangeDays: number;
}

// Fixed endpoint set the BE buckets into — keep a stable order + colour.
const ENDPOINT_ORDER = ['livescores', 'fixtures', 'fixture_detail', 'player', 'other'];
const ENTITY_LABELS: Record<string, string> = {
    livescores: 'Live scores',
    fixtures: 'Fixtures list',
    fixture_detail: 'Fixture detail',
    player: 'Player',
    other: 'Other',
};
const ENDPOINT_COLORS: Record<string, string> = {
    livescores: '#0ca7eb',
    fixtures: '#22c55e',
    fixture_detail: '#a855f7',
    player: '#f59e0b',
    other: '#94a3b8',
};
const RANGES = [1, 7, 31];

// Live-score poll cadence — an app_settings row (GET/PUT /admin/app-settings), not a
// SportMonks endpoint. It lives on this page because it is what drives the usage above:
// one value sets BOTH the backend's SportMonks poller AND the mobile app's refresh timers.
// Backend rejects anything outside this range with a 400 (message surfaced below).
interface AppSettingRow { key: string; value: string | null; }
const POLL_KEY = 'sm_live_poll_ms';
const POLL_MIN = 5_000;
const POLL_MAX = 300_000;
const POLL_DEFAULT = 15_000;

const LivePollSetting: React.FC = () => {
    const [value, setValue] = useState<string>('');
    const [saved, setSaved] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    // The key only exists once the API build that knows it is deployed. Until then the
    // backend silently ignores it on PUT — say so instead of pretending the save worked.
    const [unsupported, setUnsupported] = useState<boolean>(false);

    const load = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);
            const res: any = await api.get('/app-settings');
            const rows: AppSettingRow[] = Array.isArray(res.data) ? res.data : [];
            const row = rows.find((r) => r.key === POLL_KEY);
            setUnsupported(!row);
            const v = row?.value || String(POLL_DEFAULT);
            setValue(v);
            setSaved(v);
        } catch (err) {
            console.error('App settings API Error:', err);
            toast.error('Failed to load the live-score refresh interval');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const parsed = parseInt(value, 10);
    const invalid = !Number.isFinite(parsed) || parsed < POLL_MIN || parsed > POLL_MAX;
    const dirty = value.trim() !== saved.trim();
    // Live poll ticks per hour — the floor of what this cadence costs in SportMonks
    // requests (each tick also fetches detail per live fixture).
    const ticksPerHour = !invalid ? Math.round(3_600_000 / parsed) : 0;

    const save = async (): Promise<void> => {
        if (invalid) return;
        try {
            setSaving(true);
            const res: any = await api.put('/app-settings', { settings: { [POLL_KEY]: String(parsed) } });
            const rows: AppSettingRow[] = Array.isArray(res.data) ? res.data : [];
            const row = rows.find((r) => r.key === POLL_KEY);
            if (!row) {
                setUnsupported(true);
                toast.error('This API build does not support sm_live_poll_ms yet — value not saved.');
                return;
            }
            setUnsupported(false);
            setValue(row.value || '');
            setSaved(row.value || '');
            toast.success('Live-score refresh interval updated');
        } catch (err: any) {
            // Backend 400: "sm_live_poll_ms must be between 5000 and 300000 ms"
            toast.error(err?.response?.data?.message || 'Failed to save the interval');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white p-4 md:p-6 rounded-lg shadow mb-8">
            <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
                <FiClock className="text-primary-500" /> Live score refresh interval
            </h3>
            <p className="text-sm text-gray-500 mb-4">
                How often live scores refresh — both the backend&apos;s SportMonks poller and the app&apos;s live
                screens. Applies within one poll cycle; no restart or app release needed.
            </p>

            <div className="flex flex-wrap items-end gap-4">
                <div>
                    <label htmlFor="sm_live_poll_ms" className="block text-xs font-medium text-gray-600 mb-1">
                        Interval (ms)
                    </label>
                    <input
                        id="sm_live_poll_ms"
                        type="number"
                        min={POLL_MIN}
                        max={POLL_MAX}
                        step={1000}
                        disabled={loading || saving}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        className={`w-44 rounded-lg border px-3 py-2 text-sm disabled:bg-gray-50 ${
                            invalid && value !== '' ? 'border-red-400' : 'border-gray-300'
                        }`}
                    />
                    <div className="text-xs text-gray-400 mt-1">
                        15000 = 15 seconds (default). Allowed: {POLL_MIN.toLocaleString()}–
                        {POLL_MAX.toLocaleString()} ms (5 seconds – 5 minutes).
                    </div>
                </div>
                <button
                    onClick={save}
                    disabled={loading || saving || invalid || !dirty}
                    className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
                {!invalid && (
                    <span className="text-sm text-gray-500 pb-2">
                        = every {(parsed / 1000).toFixed(parsed % 1000 === 0 ? 0 : 1)}s &middot; ~
                        {ticksPerHour.toLocaleString()} live polls/hour
                    </span>
                )}
            </div>

            {invalid && value !== '' && (
                <p className="text-sm text-red-600 mt-3">
                    sm_live_poll_ms must be between {POLL_MIN} and {POLL_MAX} ms
                </p>
            )}

            <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                <FiAlertTriangle className="mt-0.5 shrink-0" />
                <span>
                    Lowering this increases load on both sides: the SportMonks quota shown below, and our own backend —
                    every app user&apos;s live screen refreshes at this rate too. Keep 15000 until the backend is scaled
                    up.
                </span>
            </div>

            {unsupported && !loading && (
                <p className="text-sm text-red-600 mt-3">
                    This environment&apos;s API does not have the <code>sm_live_poll_ms</code> setting yet — saving here
                    will be ignored until the backend change is deployed.
                </p>
            )}
        </div>
    );
};

// UTC 'YYYY-MM-DDTHH' -> local display
function fmtHour(h: string, withDate = false): string {
    const d = new Date(`${h}:00:00Z`);
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return withDate ? `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}` : time;
}

// Local clock time from an ISO string.
function fmtClock(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// "Xs ago" / "Xm ago" — `now` is threaded in so it re-renders live.
function fmtAgo(iso: string | null, now: number): string {
    if (!iso) return '—';
    const ms = now - new Date(iso).getTime();
    if (ms < 0) return 'just now';
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
}

// Live countdown to a reset timestamp.
function fmtCountdown(iso: string | null, now: number): string {
    if (!iso) return '—';
    const ms = new Date(iso).getTime() - now;
    if (ms <= 0) return 'due';
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

const SportMonksUsage: React.FC = () => {
    const [days, setDays] = useState<number>(7);
    const [data, setData] = useState<UsagePayload | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    // Ticks every second so countdowns / "x ago" stay live between fetches.
    const [now, setNow] = useState<number>(() => Date.now());

    const fetchUsage = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/sportmonks/usage', { params: { days } });
            setData(response.data);
        } catch (err) {
            console.error('SportMonks usage API Error:', err);
            setError('Failed to load SportMonks usage. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [days]);

    // refetch on mount + whenever the range changes
    useEffect(() => {
        fetchUsage();
    }, [fetchUsage]);

    // keep the numbers live
    useEffect(() => {
        const id = setInterval(fetchUsage, 30_000);
        return () => clearInterval(id);
    }, [fetchUsage]);

    // tick the clock for countdowns
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1_000);
        return () => clearInterval(id);
    }, []);

    if (loading && !data) {
        return (
            <div className="p-4 md:p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="flex items-center space-x-2">
                        <FiRefreshCw className="animate-spin text-primary-500 text-xl" />
                        <span className="text-gray-600">Loading SportMonks usage...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="p-4 md:p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-4 md:p-6">
                <div className="text-center text-gray-600">No usage data available.</div>
            </div>
        );
    }

    const { entityRates, throttles, poller, history, peakHour } = data;

    const chartData = {
        labels: history.map((h) => fmtHour(h.hour, days > 1)),
        datasets: ENDPOINT_ORDER.map((ep) => ({
            label: ep,
            data: history.map((h) => h.byEndpoint[ep] || 0),
            backgroundColor: ENDPOINT_COLORS[ep],
        })),
    };
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { stacked: true, ticks: { maxTicksLimit: 16, autoSkip: true } },
            y: { stacked: true, beginAtZero: true },
        },
        plugins: { legend: { position: 'bottom' as const } },
    };

    // Poller health badges.
    const gap = poller?.pacingGapMs ?? 0;
    const paced = gap > 0;

    return (
        <div className="p-4 md:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    SportMonks API Usage
                </h2>
                <button
                    onClick={fetchUsage}
                    className="flex items-center border px-3 py-1.5 rounded-full hover:border-primary-500 focus:outline-none space-x-2 text-primary-600 hover:text-primary-700 text-sm"
                >
                    <FiRefreshCw className={`text-sm ${loading ? 'animate-spin' : ''}`} />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Poller health */}
            {poller && (
                <div className="bg-white p-4 md:p-6 rounded-lg shadow mb-8">
                    <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <FiActivity className="text-primary-500" /> Poller health
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
                        <HealthStat
                            label="Token"
                            value={poller.tokenConfigured ? 'Configured' : 'Missing'}
                            good={poller.tokenConfigured}
                            bad={!poller.tokenConfigured}
                        />
                        <HealthStat
                            label="Poller"
                            value={poller.running ? (poller.isPoller ? 'Running' : 'Standby') : 'Stopped'}
                            good={poller.running && poller.isPoller}
                            bad={!poller.running}
                        />
                        <HealthStat
                            label="Pacing"
                            value={paced ? `Throttled · ${gap}ms gap` : 'Full speed'}
                            good={!paced}
                            warn={paced}
                        />
                        <HealthStat label="Live fixtures" value={String(poller.liveFixtures ?? 0)} />
                        <HealthStat label="Last poll" value={fmtAgo(poller.lastPollAt, now)} />
                        <HealthStat label="Last window sync" value={fmtAgo(poller.lastWindowSyncAt, now)} />
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                        Poll cadence {Math.round((poller.livePollMs ?? 0) / 1000)}s &middot; {poller.trackedLive ?? 0}{' '}
                        fixtures tracked for finalisation.
                    </p>
                </div>
            )}

            {/* Throttle / errors */}
            <div
                className={`p-4 md:p-6 rounded-lg shadow mb-8 border ${
                    throttles?.total ? 'bg-red-50 border-red-200' : 'bg-white border-transparent'
                }`}
            >
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                        <FiZap className={throttles?.total ? 'text-red-500' : 'text-gray-400'} /> Rate-limit throttles
                        (this hour)
                    </h3>
                    <span className={`text-lg font-bold ${throttles?.total ? 'text-red-600' : 'text-gray-700'}`}>
                        {(throttles?.total ?? 0).toLocaleString()}
                    </span>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                    SportMonks 400 &quot;Too Many Attempts.&quot; / 429 hits. Above zero means bursts are exceeding the
                    per-window rate — calls are paced &amp; retried, but sustained hits mean the poll rate or plan needs
                    attention.
                </p>
                {throttles?.total ? (
                    <div className="flex flex-wrap gap-3">
                        {ENDPOINT_ORDER.filter((ep) => throttles.byEntity[ep]?.count).map((ep) => (
                            <div
                                key={ep}
                                className="flex items-center gap-2 bg-white border border-red-200 rounded-lg px-3 py-2"
                            >
                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: ENDPOINT_COLORS[ep] }} />
                                <span className="text-sm text-gray-700">{ENTITY_LABELS[ep] || ep}</span>
                                <span className="text-sm font-semibold text-red-600">
                                    {throttles.byEntity[ep].count.toLocaleString()}
                                </span>
                                <span className="text-xs text-gray-400">
                                    last {fmtAgo(throttles.byEntity[ep].lastAt, now)}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-green-600 flex items-center gap-2">
                        <FiCheckCircle /> No throttling this hour.
                    </p>
                )}
            </div>

            {/* Per-entity SportMonks quota (real, window-synced) */}
            <div className="bg-white p-4 md:p-6 rounded-lg shadow mb-8">
                <h3 className="font-semibold text-lg mb-1">SportMonks quota (per entity)</h3>
                <p className="text-sm text-gray-500 mb-4">
                    Live from SportMonks&apos; own <code>x-ratelimit</code> headers — each entity is metered separately.
                    Usage and reset are synced to SportMonks&apos; real hourly window (detected from its counter), not our
                    clock hour.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 border-b">
                                <th className="py-2 pr-4 font-medium">Entity</th>
                                <th className="py-2 pr-4 font-medium">Used</th>
                                <th className="py-2 pr-4 font-medium">Remaining</th>
                                <th className="py-2 pr-4 font-medium">Limit</th>
                                <th className="py-2 pr-4 font-medium w-40">This window</th>
                                <th className="py-2 pr-4 font-medium">Window started</th>
                                <th className="py-2 font-medium">Resets in</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ENDPOINT_ORDER.map((ep) => {
                                const r = entityRates?.[ep];
                                if (!r || (r.limit == null && r.remaining == null)) {
                                    return (
                                        <tr key={ep} className="border-b last:border-0 text-gray-400">
                                            <td className="py-2 pr-4">
                                                <span className="inline-flex items-center gap-2">
                                                    <span
                                                        className="w-2.5 h-2.5 rounded-full"
                                                        style={{ background: ENDPOINT_COLORS[ep] }}
                                                    />
                                                    {ENTITY_LABELS[ep] || ep}
                                                </span>
                                            </td>
                                            <td className="py-2 pr-4" colSpan={6}>
                                                no request yet this session
                                            </td>
                                        </tr>
                                    );
                                }
                                const limit = r.limit ?? 0;
                                const remaining = r.remaining ?? 0;
                                const used = Math.max(0, limit - remaining);
                                const pct = limit > 0 ? (used / limit) * 100 : 0;
                                const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-primary-500';
                                // Hourly per-entity quota exhausted — SportMonks will reject further
                                // calls to this entity until the window resets.
                                const maxReached = limit > 0 && remaining <= 0;
                                return (
                                    <tr key={ep} className={`border-b last:border-0 ${maxReached ? 'bg-red-50' : ''}`}>
                                        <td className="py-2 pr-4">
                                            <span className="inline-flex items-center gap-2 text-gray-700">
                                                <span
                                                    className="w-2.5 h-2.5 rounded-full"
                                                    style={{ background: ENDPOINT_COLORS[ep] }}
                                                />
                                                {ENTITY_LABELS[ep] || ep}
                                                {maxReached && (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide">
                                                        <FiAlertTriangle className="text-[10px]" /> Max reached this hour
                                                    </span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-4 font-medium text-gray-800">{used.toLocaleString()}</td>
                                        <td className={`py-2 pr-4 ${maxReached ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>{remaining.toLocaleString()}</td>
                                        <td className="py-2 pr-4 text-gray-600">{limit.toLocaleString()}</td>
                                        <td className="py-2 pr-4">
                                            <div className="w-32 bg-gray-100 rounded-full h-2">
                                                <div
                                                    className={`${barColor} h-2 rounded-full transition-all`}
                                                    style={{ width: `${Math.min(100, pct)}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-gray-400">{pct.toFixed(1)}%</span>
                                        </td>
                                        <td className="py-2 pr-4 text-gray-600">{fmtClock(r.windowStartedAt)}</td>
                                        <td className="py-2 text-gray-600">{fmtCountdown(r.windowResetsAt, now)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* History chart (our request log, per clock hour) */}
            <div className="bg-white p-4 md:p-6 rounded-lg shadow">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-lg">Our requests per hour</h3>
                    <div className="flex items-center gap-1">
                        {RANGES.map((r) => (
                            <button
                                key={r}
                                onClick={() => setDays(r)}
                                className={`px-3 py-1 rounded-full text-sm border ${
                                    days === r
                                        ? 'bg-primary-500 text-white border-primary-500'
                                        : 'text-gray-600 hover:border-primary-500'
                                }`}
                            >
                                {r}d
                            </button>
                        ))}
                    </div>
                </div>
                <p className="text-xs text-gray-400 mb-4 flex items-center gap-2">
                    <FiTrendingUp className="shrink-0" />
                    Calls we made, bucketed by clock hour (our log — a trend view, not synced to SportMonks&apos; reset
                    window).
                    {peakHour ? ` Peak ${peakHour.total.toLocaleString()} at ${fmtHour(peakHour.hour, true)}.` : ''}
                </p>
                <div className="h-72">
                    {history.length > 0 ? (
                        <Bar data={chartData} options={chartOptions} />
                    ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                            No requests recorded in this range yet.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Small labelled status cell for the poller-health grid.
const HealthStat: React.FC<{ label: string; value: string; good?: boolean; bad?: boolean; warn?: boolean }> = ({
    label,
    value,
    good,
    bad,
    warn,
}) => {
    const color = bad ? 'text-red-600' : warn ? 'text-amber-600' : good ? 'text-green-600' : 'text-gray-800';
    const Icon = bad ? FiXCircle : good ? FiCheckCircle : null;
    return (
        <div>
            <p className="text-xs text-gray-400 mb-0.5">{label}</p>
            <p className={`font-semibold flex items-center gap-1 ${color}`}>
                {Icon && <Icon className="shrink-0" />}
                {value}
            </p>
        </div>
    );
};

// The setting card renders independently of the usage fetch, so the interval stays
// editable even if /sportmonks/usage is unavailable.
const SportMonks: React.FC = () => (
    <>
        <div className="p-4 md:p-6 pb-0">
            <LivePollSetting />
        </div>
        <SportMonksUsage />
    </>
);

export default SportMonks;
